// Shared upload policy used across SaleUploads, CompactDocumentGrid,
// RoadTaxWorkbench, AccountsWorkspace, BackOfficeWorkspace, SparesWorkspace
// so the dealership rule is enforced consistently regardless of which screen
// the user is on.
//
//   pdfOnly       — client-side rejects anything other than application/pdf
//   acceptAttr    — input[accept] value for the hidden file picker
//   acceptLabel   — short suffix shown on the Upload button (e.g. ".pdf")
//   helper        — caption text under the slot
//   allowCamera   — surfaces a "Take Photo" button with capture="environment"
//   pdfOnDownload — when stored file is image/*, download should serve ?as=pdf
//
// Naming follows the upload type strings used by the backend (see
// ``UPLOAD_TYPES`` + ``PDF_ONLY_UPLOAD_TYPES`` + ``IMAGE_TO_PDF_ON_DOWNLOAD``).

export const PDF_ONLY_POLICY = {
  acceptAttr: "application/pdf",
  acceptLabel: ".pdf",
  helper: "Accepted format: PDF only",
  pdfOnly: true,
};

export const IMAGE_OR_PDF_WITH_CAMERA_POLICY = {
  acceptAttr: "image/*,application/pdf",
  acceptLabel: "image / .pdf",
  helper: "JPG, PNG or PDF · Camera capture supported",
  allowCamera: true,
  pdfOnDownload: true,
};

export const ANY_POLICY = {
  acceptAttr: "image/*,application/pdf",
  acceptLabel: "image / .pdf",
  helper: "JPG, PNG or PDF",
};

const TYPE_POLICY = {
  aadhar: PDF_ONLY_POLICY,
  pan: PDF_ONLY_POLICY,
  other: PDF_ONLY_POLICY,
  other_2: PDF_ONLY_POLICY,
  form_20: PDF_ONLY_POLICY,
  form_22: PDF_ONLY_POLICY,
  // Photo-style uploads — camera + PDF-on-download.
  passport_photo: IMAGE_OR_PDF_WITH_CAMERA_POLICY,
  vin_photo: IMAGE_OR_PDF_WITH_CAMERA_POLICY,
};

export const getUploadPolicy = (type) => TYPE_POLICY[type] || ANY_POLICY;

/** Returns ``null`` when the file is allowed, otherwise an error string. */
export function validateUpload(type, file) {
  if (!file) return "No file selected";
  const policy = getUploadPolicy(type);
  if (policy.pdfOnly) {
    const isPdf =
      (file.type || "").toLowerCase() === "application/pdf" ||
      /\.pdf$/i.test(file.name || "");
    if (!isPdf) return "Only PDF files are allowed.";
  }
  return null;
}

/** Build the download URL — adds ``as=pdf`` for image files when the type
 *  policy requires PDF on download. Uses ``&`` if the base URL already
 *  carries a query string (e.g. the auth ``?t=<jwt>`` appended by
 *  ``Files.url``), otherwise ``?``. */
export function downloadUrlFor(type, file, baseUrl) {
  const policy = getUploadPolicy(type);
  const isImage = (file?.content_type || "").startsWith("image/");
  if (policy.pdfOnDownload && isImage) {
    const sep = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${sep}as=pdf`;
  }
  return baseUrl;
}
